
import React, { ReactNode, useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Wallet,
  History,
  BadgeDollarSign,
  ArrowLeftRight,
  Upload,
  Download,
  ImagePlus,
  FileCheck,
  Ticket,
  LogOut,
  Share2,
  Users2,
  PiggyBank,
  Diamond,
  LucideIcon,
  MessageCircle,
  Tag,
  Shield,
  MessageCircleQuestion,
  CoinsIcon,
  Gift,
  MailIcon,
  Settings,
  ArrowDownIcon,
  Activity,
  X,
  Smartphone,
  Clock as TimeIcon,
  UserCheck,
  CalendarRange,
  Database,
  // Tag,
} from "lucide-react";
import api from "../utils/api";
import { API_BASE } from "../config";

interface NavItemProps {
  to: string;
  icon: LucideIcon;
  children: ReactNode;
}

interface NavGroupProps {
  title: string;
  children: ReactNode;
}

interface SidebarLinkData {
  path: string;
  label: string;
  icon: LucideIcon;
  group: string;
}

const allSidebarLinks: SidebarLinkData[] = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard, group: "Main" },
  { path: "/users", label: "Users List", icon: Users, group: "Main" },
  { path: "/teamDetails", label: "Team Details", icon: Users2, group: "Main" },

  { path: "/teamincome", label: "Team Income", icon: BadgeDollarSign, group: "Financial" },
  { path: "/income", label: "Self Income", icon: PiggyBank, group: "Financial" },
  { path: "/referal", label: "Referal Income", icon: Share2, group: "Financial" },
  { path: "/platinumIncome", label: "Platinum Income", icon: Wallet, group: "Financial" },
  { path: "/platinumIncomeTeam", label: "Platinum Team Income", icon: Wallet, group: "Financial" },
  { path: "/transferDetails", label: "Transfer Details", icon: ArrowLeftRight, group: "Financial" },
  { path: "/wallet", label: "User Wallet", icon: Wallet, group: "Financial" },
  { path: "/gwccoin", label: "GWC Coin", icon: CoinsIcon, group: "Financial" },
  { path: "/incomeStat", label: "Income Statistics", icon: BadgeDollarSign, group: "Financial" },

  { path: "/invest", label: "Invested Amount", icon: BadgeDollarSign, group: "Investments" },
  { path: "/investHistory", label: "Investment History", icon: History, group: "Investments" },
  { path: "/platinumHistory", label: "Platinum History", icon: History, group: "Investments" },
  { path: "/platinumData", label: "Platinum Data", icon: Diamond, group: "Investments" },

  { path: "/deposit", label: "Deposit Details", icon: Upload, group: "Transactions" },
  { path: "/withdraw", label: "Withdraw Requests", icon: Download, group: "Transactions" },
  { path: "/withdrawData", label: "Investment Withdraw", icon: History, group: "Transactions" },


  { path: "/notification", label: "Notification", icon: MessageCircle, group: "Other" },
  { path: "/mail", label: "Mailer", icon: MailIcon, group: "Other" },
  { path: "/banner", label: "App Banners", icon: ImagePlus, group: "Other" },

  { path: "/kyc", label: "User KYC", icon: FileCheck, group: "Other" },
  { path: "/ticket", label: "Ticket", icon: Ticket, group: "Other" },
  { path: "/appVersion", label: "App Version", icon: Smartphone, group: "Other" },
  { path: "/roles", label: "Roles & Responsibilities", icon: Shield, group: "Other" },
  { path: "/blogs", label: "Blogs", icon: MessageCircleQuestion, group: "Other" },
  { path: "/shiftHistory", label: "Shift History", icon: ArrowDownIcon, group: "Financial" },
  { path: "/rank", label: "Ranks", icon: Gift, group: "Rewards" },
  { path: "/offers", label: "Offers", icon: Tag, group: "Rewards" },
  { path: "/audit-logs", label: "Audit Logs", icon: Activity, group: "Other" },
  { path: "/user-logs", label: "User Logs", icon: History, group: "Other" },
  { path: "/cron-manager", label: "Cron Jobs", icon: TimeIcon || Settings, group: "Other" },
  { path: "/cron-audit", label: "Cron Audit", icon: Activity, group: "Other" },
  { path: "/targeted-cron", label: "Targeted Cron", icon: UserCheck, group: "Other" },
  { path: "/daterange-cron", label: "Date Range Cron", icon: CalendarRange, group: "Other" },
  { path: "/income-date-shift", label: "Income Date Shift", icon: CalendarRange, group: "Other" },
  { path: "/recovery-payout", label: "Recovery Payout", icon: BadgeDollarSign, group: "Financial" },
  { path: "/planCards", label: "Plan Cards", icon: Tag, group: "Other" },
  { path: "/settings", label: "Settings", icon: Settings, group: "Other" },

  { path: "/wallet-backup", label: "Wallet Backup", icon: Database, group: "Wallet Management" },
];

interface SidebarProps {
  onMobileClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onMobileClose }) => {
  const navigate = useNavigate();
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [userName, setUserName] = useState<string>("Admin User");
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);

  const scrollRef = React.useRef<HTMLDivElement>(null);

  // Fetch latest permissions from backend on mount
  useEffect(() => {
    const fetchMe = async () => {
      try {
        const response = await api.get('/api/auth/me');
        const data = response.data;

        if (data.status === 'success') {
          setUserPermissions(data.permissions);
          setIsSuperAdmin(data.isSuperAdmin);
          setUserName(data.name);

          // Update localStorage as a cache, but the state is our source of truth
          localStorage.setItem("permissions", JSON.stringify(data.permissions));
          localStorage.setItem("isSuperAdmin", data.isSuperAdmin.toString());
          localStorage.setItem("name", data.name);
          localStorage.setItem("role", data.role);
        }
      } catch (error: any) {
        console.error("Error fetching user profile:", error);
        if (error.response?.status === 401) {
          handleLogout();
        }
      }
    };

    fetchMe();
  }, []);

  // Restore scroll position on mount/update
  React.useLayoutEffect(() => {
    const savedScroll = sessionStorage.getItem("sidebarScrollY");
    if (savedScroll && scrollRef.current) {
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = Number(savedScroll);
        }
      }, 0);
    }
  }, [userPermissions]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    sessionStorage.setItem("sidebarScrollY", scrollTop.toString());
  };

  const handleLogout = async (): Promise<void> => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error("Error during server logout audit:", error);
    }

    // Clear all localStorage items
    localStorage.clear();

    // Navigate to login page
    navigate("/login");
  };

  // Filter sidebar links based on user permissions
  const getFilteredLinks = (group: string): SidebarLinkData[] => {
    return allSidebarLinks.filter(link => {
      // Basic group filter
      if (link.group !== group) return false;

      // Special case for audit logs and wallet backup - ONLY Super Admin
      if (link.path === '/audit-logs') return isSuperAdmin;
      if (link.path === '/wallet-backup') return isSuperAdmin;
      if (link.path === '/recovery-payout') return isSuperAdmin;

      // Standard permission check
      return isSuperAdmin || userPermissions.includes(link.path);
    });
  };

  const NavItem: React.FC<NavItemProps> = ({ to, icon: Icon, children }) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-3 py-2.5 px-4 rounded-lg transition-all duration-200 hover:bg-[#523680]/20 ${isActive ? "bg-[#523680] text-white hover:bg-[#523680]" : "text-gray-600"
        }`
      }
    >
      <Icon size={20} />
      <span className="text-sm font-medium">{children}</span>
    </NavLink>
  );

  const NavGroup: React.FC<NavGroupProps> = ({ title, children }) => (
    <div className="mb-4">
      <h3 className="text-xs font-semibold text-gray-400 uppercase px-4 mb-2">{title}</h3>
      {children}
    </div>
  );

  // Function to render nav groups only if they have visible items
  const renderNavGroup = (title: string, groupName: string) => {
    const filteredLinks = getFilteredLinks(groupName);

    if (filteredLinks.length === 0) {
      return null;
    }

    return (
      <NavGroup title={title}>
        {filteredLinks.map(link => (
          <NavItem key={link.path} to={link.path} icon={link.icon}>
            {link.label}
          </NavItem>
        ))}
      </NavGroup>
    );
  };

  return (
    <div className="w-64 h-full bg-white shadow-lg flex flex-col">
      {/* Logo Section */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-lg font-bold text-[#523680]">
                Growwin<span className="text-[#b9aecc]">capital</span>
              </h2>
              <p className="text-xs text-[#b9aecc]">Investment Platform</p>
            </div>
          </div>
          {/* Mobile Close Button */}
          {onMobileClose && (
            <button
              onClick={onMobileClose}
              className="lg:hidden p-2 text-gray-500 hover:bg-gray-100 rounded-md"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          )}
        </div>
      </div>

      {/* User Info Section */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#523680] flex items-center justify-center text-white font-semibold">
            {(userName || 'A').charAt(0).toUpperCase()}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-800">{userName}</p>
            <p className="text-xs text-gray-500">
              {isSuperAdmin ? "Super Admin" : "Admin"}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Section - Scrollable */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-2 py-4 space-y-2"
      >
        {renderNavGroup("Main", "Main")}
        {renderNavGroup("Wallet Management", "Wallet Management")}
        {renderNavGroup("Financial", "Financial")}
        {renderNavGroup("Investments", "Investments")}
        {renderNavGroup("Transactions", "Transactions")}
        {renderNavGroup("Rewards", "Rewards")}
        {renderNavGroup("Other", "Other")}
      </div>

      {/* Logout Button - Fixed at Bottom */}
      <div className="border-t border-gray-100 p-4">
        <button
          onClick={handleLogout}
          className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-lg text-white bg-[#523680] hover:bg-[#523680]/90 transition-colors duration-200"
        >
          <LogOut size={20} />
          <span className="font-medium text-white">Logout</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;